// import { Selection } from '@leverege/ui-redux'
import { Selection } from '@leverege/ui-redux';
import { GeoshapeStyle } from '@leverege/ui-google-maps-elements';
import { v4 } from '@leverege/base62-util';
import GeoPointAdapter from "./GeoPointAdapter.js";
import Util from "./Util.js";
import Model from "../../shared/renderers/HistoryModel.js";
import HistoryAccumulators from "../../shared/renderers/accumulator/HistoryAccumulators.js";
const ARROW_DISTANCE_OFFSET = 300;
const {
  getLineColor,
  getLineWidth,
  getLinePattern,
  getLineOpacity,
  getLineCap,
  getLineJoin,
  getLineStyle,
  getSelectedLineColor,
  getSelectedLineWidth,
  getSelectedLinePattern,
  getSelectedLineOpacity,
  getSelectedLineCap,
  getSelectedLineJoin,
  getSelectedLineStyle,
  getRolloverLineColor,
  getRolloverLineWidth,
  getRolloverLinePattern,
  getRolloverLineOpacity,
  getRolloverLineCap,
  getRolloverLineJoin,
  getRolloverLineStyle
} = Model;
class HistoryAdapter extends GeoPointAdapter {
  constructor(opts) {
    super(opts);
    this.Model = Model;
    this.attributeName = opts?.attributeName;
    this._geometry = null;
    this.getGeometry();
  }
  getGeometry() {
    if (this._geometry) {
      return this._geometry;
    }
    window.google.maps.importLibrary('geometry').then(geometry => {
      this._geometry = geometry;
      return geometry;
    });
    return null;
  }
  getShapeData(item, id, options) {
    const {
      model
    } = options;
    const geometry = this.getGeometry();
    if (!geometry) {
      return null;
    }
    const lineCoordinates = [];
    const pts = HistoryAccumulators.getPoints(Model.getAccumulator(model), item.items, options);
    const points = pts.reduce(this.getShapeDataReducer(options, lineCoordinates), []);
    const lines = this.getLinesFromLineCoordinates(lineCoordinates);
    const arrows = this.getArrowsFromPoints(points, options);
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].geometry.coordinates.length) {
        const app = {
          visible: true,
          name: null,
          showOutline: true
        };
        let {
          model
        } = options;
        model = model || {};
        if (lines[i].selected) {
          app.lineColor = Util.getColor(getSelectedLineColor(model), item);
          app.lineOpacity = Util.getNumber(getSelectedLineOpacity(model), item);
          app.linePattern = Util.getPattern(getSelectedLinePattern(model), item);
          app.lineWidth = Util.getNumber(getSelectedLineWidth(model), item);
          app.lineCap = getSelectedLineCap(model) || 'butt';
          app.lineJoin = getSelectedLineJoin(model) || 'mitre';
          app.lineStyle = getSelectedLineStyle(model) || 'default';
          app.textColor = app.lineColor;
          app.textSize = app.lineWidth * 10;
          app.layerGroup = 'selected';
        } else if (lines[i].rollover) {
          app.lineColor = Util.getColor(getRolloverLineColor(model), item);
          app.lineOpacity = Util.getNumber(getRolloverLineOpacity(model), item);
          app.linePattern = Util.getPattern(getRolloverLinePattern(model), item);
          app.lineWidth = Util.getNumber(getRolloverLineWidth(model), item);
          app.lineCap = getRolloverLineCap(model) || 'butt';
          app.lineJoin = getRolloverLineJoin(model) || 'mitre';
          app.lineStyle = getRolloverLineStyle(model) || 'default';
          app.textColor = app.lineColor;
          app.textSize = app.lineWidth * 10;
          app.layerGroup = 'rollover';
        } else {
          app.lineColor = Util.getColor(getLineColor(model), item);
          app.lineOpacity = Util.getNumber(getLineOpacity(model), item);
          app.linePattern = Util.getPattern(getLinePattern(model), item);
          app.lineWidth = Util.getNumber(getLineWidth(model), item);
          app.lineCap = getLineCap(model) || 'butt';
          app.lineJoin = getLineJoin(model) || 'mitre';
          app.lineStyle = getLineStyle(model) || 'default';
        }
        points.push({
          geoJson: lines[i],
          appearance: app
        });
      }
    }
    if (arrows && arrows.length > 0) {
      points.push(...arrows);
    }
    return points;
  }
  getShapeDataReducer(options, lineCoordinates) {
    return (prev, curr) => {
      let accum;
      let raw;

      // const { selected } = options

      if (curr.accum) {
        accum = curr.accum;
        raw = curr.raw; // eslint-disable-line
      } else {
        accum = curr;
      }
      let parentShapeData = super.getShapeData(accum, accum.id, options);
      parentShapeData = Array.isArray(parentShapeData) ? parentShapeData[0] : parentShapeData;
      const coordinates = parentShapeData?.geoJson?.geometry?.coordinates;
      const selected = Selection.isSelected(options.selected, accum.id);
      const rollover = Selection.isSelected(options.rollover, accum.id);
      if (coordinates) {
        lineCoordinates.push({
          coordinates,
          selected,
          rollover
        });
      }
      if (parentShapeData) {
        prev.push({
          ...parentShapeData,
          appearance: {
            ...parentShapeData.appearance,
            dataId: accum.id
          }
        });

        // Uncomment to show rollup points on map when selected

        // if ( Array.isArray( raw ) && raw.length > 0 ) {
        //   raw.forEach( ( r ) => {
        //     const rShapeData = super.getShapeData( r, accum.id, options )
        //     prev.push( {
        //       ...rShapeData,
        //       appearance : {
        //         ...rShapeData.appearance,
        //         visible : Selection.isSelected( selected, accum.id )
        //       }
        //     } )
        //   } )
        // }
      }
      return prev;
    };
  }
  getLinesFromLineCoordinates(lineCoordinates) {
    const lines = [{
      id: v4(),
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: []
      },
      selected: false,
      rollover: false
    }];
    for (let i = 0; i < lineCoordinates.length; i++) {
      if (lineCoordinates[i].rollover || lineCoordinates[i].selected) {
        lines.push({
          id: v4(),
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: []
          },
          selected: lineCoordinates[i].selected,
          rollover: lineCoordinates[i].rollover
        });

        // Add previous coordinate if available.
        if (i > 0) {
          lines[lines.length - 1].geometry.coordinates.push(lineCoordinates[i - 1].coordinates);
        }

        // Add current coordinate.
        lines[lines.length - 1].geometry.coordinates.push(lineCoordinates[i].coordinates);

        // Add next coordinate if available.
        if (i < lineCoordinates.length - 1) {
          lines[lines.length - 1].geometry.coordinates.push(lineCoordinates[i + 1].coordinates);
        }
        lines.push({
          id: v4(),
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: []
          },
          selected: false,
          rollover: false
        });
      } else {
        lines[lines.length - 1].geometry.coordinates.push(lineCoordinates[i].coordinates);
      }
    }
    return lines;
  }
  getArrowsFromPoints(points, options) {
    const {
      map
    } = options;
    if (points.length > 1) {
      const geometry = this.getGeometry();
      const arrows = [];
      for (let i = 0; i < points.length; i++) {
        if (i === 0) {
          continue;
        }
        const point = points[i];
        const nextPoint = points[i - 1];
        const isRollover = point.appearance.isRollover || nextPoint.appearance.isRollover;
        if (point && nextPoint) {
          const latLng = {
            lat: point.geoJson.geometry.coordinates[1],
            lng: point.geoJson.geometry.coordinates[0]
          };
          const nextLatLng = {
            lat: nextPoint.geoJson.geometry.coordinates[1],
            lng: nextPoint.geoJson.geometry.coordinates[0]
          };
          const screenPoint = Util.latLng2Point(latLng, map);
          const nextScreenPoint = Util.latLng2Point(nextLatLng, map);
          if (!screenPoint || !nextScreenPoint) {
            continue;
          }
          const d = Math.sqrt((nextScreenPoint.x - screenPoint.x) ** 2 + (nextScreenPoint.y - screenPoint.y) ** 2);
          const segments = Math.min(Math.max(Math.floor(d * 10 / ARROW_DISTANCE_OFFSET) - 1, 2), 1000);
          for (let i = 1; i < segments; i++) {
            const arrow = geometry.spherical.interpolate(latLng, nextLatLng, i / segments).toJSON();
            const heading = geometry.spherical.computeHeading(latLng, arrow);
            arrows.push({
              geoJson: {
                id: v4(),
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'Point',
                  coordinates: [arrow.lng, arrow.lat]
                }
              },
              appearance: {
                visible: true,
                showSymbol: true,
                showLabel: false,
                showFill: false,
                showOutline: false,
                iconAngle: -heading,
                isArrow: true,
                isSelected: false,
                isRollover,
                layerGroup: 'arrows'
              }
            });
          }
        }
      }
      return arrows;
    }
    return null;
  }
  getSymbolLayerOptions(style, layerGroup, options) {
    const {
      model,
      map,
      zoom
    } = options;
    const rScale = model && this.Model.getRolloverScale(model) || 1;
    const sScale = model && this.Model.getSelectedScale(model) || 1;
    const nScale = model && this.Model.getNormalScale(model) || 1;
    const show = model && this.Model.getShowAtZoom(model);
    const hide = model && this.Model.getHideAtZoom(model);
    const visible = Util.getVisible(show, hide, zoom);
    return {
      visible,
      pickable: model.rolloverable || model.selectable,
      filled: false,
      stroked: true,
      getIcon: d => {
        const symbol = d.properties.symbol;
        const isArrow = d.properties.isArrow;
        if (isArrow) {
          return {
            url: 'https://storage.googleapis.com/molten-ui-assets/history_line_arrow.svg',
            width: 64,
            height: 64
          };
        }
        return {
          url: symbol.src,
          width: symbol.width,
          height: symbol.height || symbol.width
        };
      },
      getIconAngle: d => {
        if (d.properties.iconAngle) {
          return d.properties.iconAngle + (map.getHeading() || 0);
        }
        return undefined;
      },
      getIconColor: d => {
        if (d.properties.isArrow) {
          return [0, 0, 0, d.properties.isRollover ? 255 : 0];
        }
        return [0, 0, 0, 255];
      },
      getIconSize: d => {
        const isRollover = d.properties.isRollover;
        const isSelected = d.properties.isSelected;
        const symbol = d.properties.symbol;
        const isArrow = d.properties.isArrow;
        if (isArrow) {
          return 10;
        }
        let size = 0.5 * nScale;
        if (isRollover) {
          size = 0.5 * rScale;
        } else if (isSelected) {
          size = 0.5 * sScale;
        }
        return symbol.width * size;
      }
    };
  }
  getOutlineLayerOptions(style, layerGroup, options) {
    const {
      model,
      zoom
    } = options;
    const show = model && Model.getShowAtZoom(model);
    const hide = model && Model.getHideAtZoom(model);
    const visible = Util.getVisible(show, hide, zoom);
    const lineJoin = getLineJoin(model) || 'mitre';
    const lineCap = getLineCap(model) || 'square';
    return {
      ...GeoshapeStyle.OUTLINE_STYLE,
      visible,
      stroked: true,
      filled: false,
      pickable: false,
      // model.rolloverable || model.selectable,
      lineJointRounded: lineJoin === 'round',
      lineCapRounded: lineCap === 'round',
      getLineColor: d => {
        const showOutline = d.properties.showOutline;
        const opacity = showOutline ? d.properties.lineOpacity : 0;
        return Util.getColorAndOpacity(d.properties.lineColor, opacity);
      },
      getLineWidth: d => {
        return d.properties.lineWidth || 1;
      },
      lineWidthUnits: 'pixels',
      getDashArray: d => {
        const outlineStyle = d.properties.lineStyle;
        switch (outlineStyle) {
          case 'long-dash':
            return [8, 3];
          case 'short-dash':
            return [3, 3];
          case 'dot':
            return [1, 1];
          default:
            return null;
        }
      }
    };
  }
}
export default HistoryAdapter;